import { linearGradient } from 'polished';
import { useState } from 'react';
import { css, styled } from 'styled-components';
import { FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, type SwiperProps, SwiperSlide } from 'swiper/react';
import { type Swiper as TypeOfSwiper } from 'swiper/types';

import { Image } from '../../atoms';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';

const MAX_THUMBNAILS = 4;
const GAP_SIZE = 10;
const THUMBNAIL_HEIGHT = 102;

type CarouselImageType = {
	alt?: string;
	thumbnailUrl?: string;
	url?: string;
};

export type BaseSwiperCarouselProps = {
	hasPagination?: boolean;
	/** FIXME: Thumbnails don't work properly in storybook env */
	hasThumbnails?: boolean;
	height?: number;
	images: Array<CarouselImageType>;
	onPress?: () => void;
	options?: SwiperProps;
	testID: string;
};

/**
 * BaseSwiperCarousel abstracts (swiper/react)
 * [https://github.com/nolimits4web/Swiper]
 *
 * @param {BaseSwiperCarouselProps} props - The props of the carousel
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @param {CarouselImageType[]} props.images The array of remote images
 * @param {Options} props.options Carousel component's props
 * @param {number} props.height The height of the carousel
 * @param {boolean} props.hasPagination Whether to show pagination or not
 * @param {boolean} props.hasThumbnails Whether to show thumbnails or not
 * @param {() => void} props.onPress The callback function to be called on carousel click
 * @returns {JSX.Element} The carousel
 */
export const BaseSwiperCarousel = ({
	hasPagination = false,
	hasThumbnails = true,
	height = 400,
	images = [],
	onPress,
	options,
	testID
}: BaseSwiperCarouselProps) => {
	const [thumbsSwiper, setThumbsSwiper] = useState<null | TypeOfSwiper>(null);

	const optionalModules = hasPagination ? [Pagination] : [];

	return (
		<div data-testid={testID}>
			<S.StyledSwiper
				modules={[FreeMode, Thumbs, ...optionalModules, Navigation]}
				navigation
				pagination={hasPagination ? { clickable: true } : false}
				spaceBetween={GAP_SIZE}
				style={{ height }}
				thumbs={{ swiper: thumbsSwiper }}
				onSwiper={setThumbsSwiper}
				{...options}
			>
				{images.map((image, index) => (
					<SwiperSlide key={index}>
						<S.StyledMainImage
							alt={image?.alt ?? ''}
							data-testid="galleryView-main-image"
							fill
							src={image.url ?? ''}
							onClick={onPress}
						/>
					</SwiperSlide>
				))}
				<S.BottomGradient />
			</S.StyledSwiper>

			{hasThumbnails && (
				<S.StyledThumbsSwiper
					$isCentred={images.length < 4}
					freeMode
					modules={[FreeMode, Thumbs, Navigation]}
					slidesPerView={MAX_THUMBNAILS}
					spaceBetween={GAP_SIZE}
					style={{
						height: THUMBNAIL_HEIGHT,
						marginTop: GAP_SIZE
					}}
					watchSlidesProgress
					onSwiper={setThumbsSwiper}
				>
					{images.map((image, index) => (
						<SwiperSlide key={index}>
							<Image
								alt={image?.alt ?? ''}
								data-testid={`galleryView-image${index}-button`}
								fill
								src={image.thumbnailUrl ?? ''}
								style={{ objectFit: 'cover' }}
								onClick={() => {
									onPress && onPress();
								}}
							/>
						</SwiperSlide>
					))}
				</S.StyledThumbsSwiper>
			)}
		</div>
	);
};

namespace S {
	export const StyledSwiper = styled(Swiper)`
		--swiper-pagination-bullet-inactive-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		--swiper-theme-color: ${({ theme }) => theme.colors.backgrounds.brand.enabled};
		--swiper-pagination-bullet-inactive-opacity: 1;
	`;

	export const StyledThumbsSwiper = styled(StyledSwiper)<{ $isCentred: boolean }>`
		.swiper-slide-thumb-active {
			border-color: ${({ theme }) => theme.colors.borders.brand.selected};
			border-width: ${({ theme }) => theme.border.width.large};
			border-style: solid;
		}

		${({ $isCentred }) =>
			$isCentred &&
			css`
				.swiper-wrapper {
					justify-content: center};
				}
			`};
	`;

	export const StyledMainImage = styled(Image)`
		object-fit: cover;
	`;

	export const BottomGradient = styled.div`
		z-index: 1;
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: ${({ theme }) => theme.spacings.xxlarge};
		${({ theme }) =>
			linearGradient({
				colorStops: theme.colors.gradients.darkNeutralGradient,
				toDirection: 'to bottom'
			})};
	`;
}
